import React, { useState, useEffect } from "react";
import {
  Modal,
  Table,
  Button,
  Input,
  Space,
  message,
  Popconfirm,
  Tooltip,
  Empty,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import courseService from "../../services/course.service";

const SubCategoryManager = ({ visible, categoryId, categoryName, onClose }) => {
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [subCategoryName, setSubCategoryName] = useState("");
  const [formVisible, setFormVisible] = useState(false);

  useEffect(() => {
    if (visible && categoryId) {
      loadSubCategories();
    }
  }, [visible, categoryId]);

  const loadSubCategories = async () => {
    setLoading(true);
    try {
      const data = await courseService.getSubcategoriesByCategory(categoryId);
      setSubCategories(data);
    } catch (error) {
      message.error("Erreur lors du chargement des sous-catégories");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditMode(false);
    setSelectedSubCategory(null);
    setSubCategoryName("");
    setFormVisible(true);
  };

  const handleEdit = (subCategory) => {
    setEditMode(true);
    setSelectedSubCategory(subCategory);
    setSubCategoryName(subCategory.libelle);
    setFormVisible(true);
  };

  const handleSubmit = async () => {
    if (!subCategoryName.trim()) {
      message.error("Le nom de la sous-catégorie est requis");
      return;
    }

    try {
      if (editMode) {
        await courseService.updateSubCategory(selectedSubCategory.id, {
          libelle: subCategoryName,
        });
        message.success("Sous-catégorie mise à jour avec succès");
      } else {
        await courseService.createSubCategory(categoryId, {
          libelle: subCategoryName,
        });
        message.success("Sous-catégorie créée avec succès");
      }
      setFormVisible(false);
      loadSubCategories();
    } catch (error) {
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error(
          editMode
            ? "Erreur lors de la mise à jour"
            : "Erreur lors de la création"
        );
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await courseService.deleteSubCategory(id);
      message.success("Sous-catégorie supprimée avec succès");
      loadSubCategories();
    } catch (error) {
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error("Erreur lors de la suppression");
      }
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Nom de la sous-catégorie",
      dataIndex: "libelle",
      key: "libelle",
      render: (text) => (
        <div style={{ fontWeight: 500, fontSize: "14px" }}>{text}</div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Modifier">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer cette sous-catégorie ?"
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Tooltip title="Supprimer">
              <Button danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={
          <div style={{ fontSize: "18px", fontWeight: "bold" }}>
            Sous-catégories de : {categoryName}
          </div>
        }
        open={visible}
        onCancel={onClose}
        footer={null}
        width={800}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Nouvelle Sous-catégorie
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={subCategories}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{
            emptyText: (
              <Empty
                description="Aucune sous-catégorie"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </Modal>

      <Modal
        title={
          editMode ? "Modifier la sous-catégorie" : "Nouvelle sous-catégorie"
        }
        open={formVisible}
        onOk={handleSubmit}
        onCancel={() => setFormVisible(false)}
        okText={editMode ? "Mettre à jour" : "Créer"}
        cancelText="Annuler"
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>
            Nom de la sous-catégorie *
          </div>
          <Input
            placeholder="Ex: React.js"
            value={subCategoryName}
            onChange={(e) => setSubCategoryName(e.target.value)}
            onPressEnter={handleSubmit}
          />
        </div>
      </Modal>
    </>
  );
};

export default SubCategoryManager;